import { BodyMatcherItem } from '../components/transactionEditor/BodyMatchersList';
import { AttributesNodeMatcherValue, RequestNodeWidgetType } from '../components/transactionEditor/RequestNodeWidget';
import _ from 'lodash';
import { BlazeDropdownItem } from '../components/ui/dropdown/BlazeDropdown/BlazeInputDropdown';

export interface MatcherItem {
  id: string;
  name: string;
}

export const specificMatcherItem: MatcherItem = {
  id: 'Specific',
  name: 'Specific',
};

export const anythingMatcherItem: MatcherItem = {
  id: 'Anything',
  name: 'Anything',
};

export const regexMatcherItem: MatcherItem = {
  id: 'Regex',
  name: 'Regex',
};

export const matcherTypesDropdownItems: BlazeDropdownItem[] = [
  specificMatcherItem,
  anythingMatcherItem,
  regexMatcherItem,
];

const specificXpathSnippet = xmlPath => `[[${xmlPath}/text()`;

const specificXpathForAttribute = xmlPath => `[[${xmlPath}/@`;

const specificJsonPathSnippet = jsonPath => `[[$${jsonPath},`;

export const isLeafSelected = (
  leafNode: any,
  bodyMatcherList: BodyMatcherItem[],
  type: RequestNodeWidgetType,
  nodeTree: any,
  cdataXpathSelected: string | null,
) => {
  return getIndexOfMatcherFromLeaf(leafNode, bodyMatcherList, type, nodeTree, cdataXpathSelected) !== -1;
};

/**
 * Goes through tree and checks if any name contains namespace.
 * @param treeNode
 */
export const containsNameSpace = (val: string) => {
  if (val && val.includes(':')) {
    return true;
  }
  return false;
};

/**
 * Gets index of the matcher which is related to provided leaf.
 *
 * @param leafNode
 * @param bodyMatcherList
 * @param type - json / xpath / cdata_xpath
 * @param nodePath - path to leafNode within the tree
 */
export const getIndexOfMatcherFromLeaf = (
  leafNode: any,
  bodyMatcherList: BodyMatcherItem[],
  type: RequestNodeWidgetType,
  nodePath: any,
  cdataXpathSelected: string | null,
) => {
  const foundIndex = _.findIndex(bodyMatcherList, (matcher: BodyMatcherItem) => {
    if (_.isNil(matcher.content) || !_.has(leafNode, 'name')) {
      return false;
    }

    const { name, leafText } = leafNode;

    if (type === 'xpath' && leafText !== undefined) {
      // We search for both paths with name spaces and also without using just local-name
      // because namespace can be ignored when path is generated
      const pathWithoutNamespaces = getXpathForNodeInTree(nodePath, false);
      const pathWithNamespaces = getXpathForNodeInTree(nodePath, true);
      return (
        matcher.content.includes(specificXpathSnippet(pathWithoutNamespaces)) ||
        matcher.content.includes(specificXpathSnippet(pathWithNamespaces))
      );
    } else if (type === 'cdata_xpath' && leafText !== undefined) {
      // If the current body matcher does not have the correct CData XPath as the one that is currently
      // selected, then don't consider it
      if (cdataXpathSelected !== matcher.cdataXpath) {
        return false;
      }

      // We search for both paths with name spaces and also without using just local-name
      // because namespace can be ignored when path is generated
      const pathWithoutNamespaces = getXpathForNodeInTree(nodePath, false);
      const pathWithNamespaces = getXpathForNodeInTree(nodePath, true);
      return (
        matcher.content.includes(specificXpathSnippet(pathWithoutNamespaces)) ||
        matcher.content.includes(specificXpathSnippet(pathWithNamespaces))
      );
    } else if (type === 'json') {
      const path = getJsonPathForNodeInTree(nodePath) + '.' + name;
      return matcher.content.includes(specificJsonPathSnippet(path));
    }
    return false;
  });
  return foundIndex;
};

export const getIndexOfMatcherFromAttribute = (
  attribute: any,
  bodyMatcherList: BodyMatcherItem[],
  type: RequestNodeWidgetType,
  nodePath: any,
  cdataXpathSelected: string | null,
) => {
  const indexInAttributesArray = _.findIndex(bodyMatcherList, (matcher: BodyMatcherItem) => {
    if (_.isNil(matcher.content) || !_.has(attribute, 'name')) {
      return false;
    }

    const { name } = attribute;
    if (type === 'xpath') {
      // We search for both paths with name spaces and also without using just local-name
      // because namespace can be ignored when path is generated
      const pathWithoutNamespaces = getXpathForNodeInTree(nodePath, false);
      const pathWithNamespaces = getXpathForNodeInTree(nodePath, true);
      return (
        (matcher.content.includes(specificXpathForAttribute(pathWithoutNamespaces)) ||
          matcher.content.includes(specificXpathForAttribute(pathWithNamespaces))) &&
        matcher.content.includes('@' + attribute.name + ',')
      );
    } else if (type === 'cdata_xpath') {
      // If the current body matcher does not have the correct CData XPath as the one that is currently
      // selected, then don't consider it
      if (cdataXpathSelected !== matcher.cdataXpath) {
        return false;
      }

      // We search for both paths with name spaces and also without using just local-name
      // because namespace can be ignored when path is generated
      const pathWithoutNamespaces = getXpathForNodeInTree(nodePath, false);
      const pathWithNamespaces = getXpathForNodeInTree(nodePath, true);
      return (
        matcher.content.includes(specificXpathForAttribute(pathWithoutNamespaces)) ||
        matcher.content.includes(specificXpathForAttribute(pathWithNamespaces))
      );
    }
    return false;
  });
  return indexInAttributesArray;
};

/**
 * Determines body matcher type for dropdown based on the existing matcher item.
 * @param matcher
 */
export const getMatcherDropdownType = (matcher: BodyMatcherItem): MatcherItem => {
  const { content } = matcher;
  //Specific matcher
  if (content.includes(`equalTo(`)) {
    return specificMatcherItem;
  } else if (content.includes(`matching(.*)`)) {
    return anythingMatcherItem;
  }
  return regexMatcherItem;
};

/**
 * Extracts value from existing matcher.
 * @param leafNode
 * @param matcher
 */
export const getMatcherValue = (leafNode: any, matcher: BodyMatcherItem) => {
  const { content } = matcher;
  const { leafText } = leafNode;
  //If matcher content is empty, use leaf name as default val
  if (_.isNil(content)) {
    return leafText;
  }

  const matcherType = getMatcherDropdownType(matcher);
  if (matcherType === specificMatcherItem) {
    //Extract the value with regex
    const specificTokens = content.match(/equalTo\((.*?)\)]]/);
    return specificTokens ? specificTokens[1] : leafText;
  } else if (matcherType === anythingMatcherItem) {
    //Leaf text is convenient value here for situations when user switches matcher type
    return leafText;
  } else {
    const matchingTokens = content.match(/matching\((.*?)\)]]/);
    return matchingTokens ? matchingTokens[1] : leafText;
  }
};

export const getAttributesElements = (matcher: BodyMatcherItem, leafNode: any) => {
  const startStr = '@';
  const endStr = ',';

  const startIndex = matcher.content.indexOf(startStr);
  const endIndex = matcher.content.indexOf(endStr, startIndex + startStr.length);
  const attributeName =
    startIndex !== -1 && endIndex !== -1 ? matcher.content.substring(startIndex + startStr.length, endIndex) : '';
  const attrNode = leafNode.attributesNode.find(attr => attr.name === attributeName);
  return attrNode;
};

/** Gets match type of the leaf based on provided list of matchers. If there is no matcher representing current leafnode,
 * use default values. */
export const getValuesFromBodyMatcher = (
  leafNode: any,
  bodyMatcherList: BodyMatcherItem[],
  type: RequestNodeWidgetType,
  nodePath: any,
  cdataXpathSelected: string | null,
  currentSampleBody?: string,
) => {
  const matcherIndex = getIndexOfMatcherFromLeaf(leafNode, bodyMatcherList, type, nodePath, cdataXpathSelected);
  if (matcherIndex !== -1) {
    const matcher = bodyMatcherList[matcherIndex];
    bodyMatcherList = bodyMatcherList.filter((list, i) => i !== matcherIndex);

    // The matcher's saved value was extracted from whichever sample body was showing in the wizard
    // when it was created. Once that sample body is edited and the tree is rebuilt, the saved value
    // no longer corresponds to what's on screen, so fall back to the freshly parsed leaf text rather
    // than resurfacing a value the user has no way to tell is stale.
    if (isSampleBodyStale(matcher, currentSampleBody)) {
      return {
        bodyMatcherValues: {
          matchValue: leafNode.leafText,
          matchType: specificMatcherItem.name,
          cdataXpath: cdataXpathSelected,
        },
        matcherListNodes: bodyMatcherList,
      };
    }

    return {
      bodyMatcherValues: {
        matchValue: getMatcherValue(leafNode, matcher),
        matchType: getMatcherDropdownType(matcher).id,
        cdataXpath: cdataXpathSelected,
      },
      matcherListNodes: bodyMatcherList,
    };
  }

  // Default values when matcher is not available
  return {
    bodyMatcherValues: {
      matchValue: leafNode.leafText,
      matchType: specificMatcherItem.name,
      cdataXpath: cdataXpathSelected,
    },
    matcherListNodes: bodyMatcherList,
  };
};

/** True when the matcher was generated from a sample body different from the one currently in the wizard. */
const isSampleBodyStale = (matcher: BodyMatcherItem, currentSampleBody?: string) =>
  !_.isNil(matcher.sampleBody) && !_.isNil(currentSampleBody) && matcher.sampleBody !== currentSampleBody;

/** Gets match type of the attribute based on provided list of matchers. If there is no matcher representing current attribute,
 * use default values. */
export const getAttributesValuesFromBodyMatcher = (
  attribute: any,
  bodyMatcherList: BodyMatcherItem[],
  type: RequestNodeWidgetType,
  nodePath: any,
  cdataXpathSelected: string | null,
  currentSampleBody?: string,
) => {
  const matcherIndex = getIndexOfMatcherFromAttribute(attribute, bodyMatcherList, type, nodePath, cdataXpathSelected);
  let matchValue: AttributesNodeMatcherValue = {
    id: attribute.id,
    matchValue: attribute.value,
    matchType: specificMatcherItem.name,
    cdataXpath: cdataXpathSelected,
  };
  if (matcherIndex !== -1) {
    const matcher = bodyMatcherList[matcherIndex];
    if (matcher.content.includes('@' + attribute.name + ',') && !isSampleBodyStale(matcher, currentSampleBody)) {
      matchValue = {
        id: attribute.id,
        matchValue: getMatcherValue(attribute.value, matcher),
        matchType: getMatcherDropdownType(matcher).id,
        cdataXpath: cdataXpathSelected,
      };
    }
    bodyMatcherList = bodyMatcherList.filter((list, i) => i !== matcherIndex);
  }
  return { attributesValuesFromBodyMatcher: matchValue, attributeMatcherList: bodyMatcherList };
};

/**
 * Creates path to leaf element in the tree. The direct path is represented by array list of nodes. First element is leaf and the
 * last element in the array is root of the tree. Last element in the array is root of the tree.
 *
 * @param nodes - ui tree representation of the json / xml
 * @param elementId - id of the element to be selected
 * @returns list of elements representing a path which lead to the selected one.
 */
export const generateDirectPathInTree = (nodes: any[], elementId: string | number) => {
  let elementToFind = elementId;
  const pathNodes = [];

  if (elementToFind === 0) {
    pathNodes.push(
      nodes.filter(function(value: any) {
        return value.id === elementToFind;
      })[0],
    );
  }

  while (elementToFind !== 0) {
    const nodeObj = nodes.filter(function(value: any) {
      return value.id === elementToFind;
    })[0];
    elementToFind = nodeObj.parentId ? nodeObj.parentId : 0;
    pathNodes.push(nodeObj);
  }

  return pathNodes;
};

const getLocalNodeName = (nodeName: string) => {
  return "*[local-name()='" + nodeName + "']";
};

export const checkForNamespaceIgnore = (nodeName: string, ignoreNameSpaces: boolean) => {
  if (containsNameSpace(nodeName)) {
    // Return node name without namespace prefix
    // if we are supposed to ignore namespaces
    if (ignoreNameSpaces === true) {
      const tokens = nodeName.split(':');
      return getLocalNodeName(tokens[1]);
    }

    //Return full name (with namespace included)
    return nodeName;
  } else {
    return getLocalNodeName(nodeName);
  }
};

/**
 * Creates an Xpath for node in the tree.
 * @param pathNodes
 * @param connector
 */
export const getXpathForNodeInTree = (pathNodes: any[], ignoreNameSpaces: boolean) => {
  let xpath = '';
  for (let i = pathNodes.length - 1; i >= 0; i--) {
    xpath += '/' + checkForNamespaceIgnore(pathNodes[i].name, ignoreNameSpaces);
  }
  return xpath;
};

/**
 * Creates JSON path for element in the tree.
 * @param pathNodes
 * @param connector
 */
export const getJsonPathForNodeInTree = (pathNodes: any[]) => {
  let jsonPath = '';
  for (let i = pathNodes.length - 1; i > 0; i--) {
    jsonPath += '.' + pathNodes[i].name;

    const child = pathNodes[i - 1];

    // If item is part of the array - this can be verified by child leafs (if they contain array index, it means
    // node is part of the array, if idx equals to -1 it means node is part of another object.
    if (pathNodes[i].type === 'NODE' && !_.isNil(child) && child.idx !== -1) {
      jsonPath += `[${child.idx}]`;
    }
  }
  return jsonPath;
};
